import {
  ProjectChat,
  type ProvisionalSubmitOutcome,
} from '@/features/projects/components/ProjectChat';
import { AssistantText } from '@/features/projects/timeline/projectTimelineBlocks';
import { cn } from '@/lib/cn';
import { useCreateAndOpenProject, useWorkComposerSourcesSlot } from '@heidi/agents';
import {
  type SubmitProjectTurnArgs,
  composerLinkedItemsAtom,
  getRoutineCreateGuidedIntro,
  useStreamedBlocks,
} from '@heidi/agents/data';
import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import { typographyVariant } from '@heidi/design-system/typography';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  buildCreateRoutineWithHeidiWirePrompt,
  buildRoutineSuggestionSeedPrompt,
} from '@heidi/agents/contracts';
import { getCreateRoutineWithHeidiArgs } from '../lib/createRoutineWithHeidi';

const COMPOSER_PREFILL_MARKER = '\u200B';

function stripComposerPrefillMarker(value: string): string {
  return value.replaceAll(COMPOSER_PREFILL_MARKER, '').trim();
}

/**
 * Instant, hardcoded first turn for Create → Routine. Looks like a started
 * chat; the backend project is created only when the user submits.
 */
export function RoutineCreateGuidedChat(): JSX.Element {
  const intl = useIntl();
  const toolbarSourcesSlot = useWorkComposerSourcesSlot();
  const intro = useMemo(() => getRoutineCreateGuidedIntro(intl.formatMessage), [intl]);
  const { visible: introBlocks, streaming } = useStreamedBlocks(intro);
  const linkedItems = useAtomValue(composerLinkedItemsAtom);
  const [optimisticFirstTurnText, setOptimisticFirstTurnText] = useState<string | null>(
    null
  );

  const handleCreateError = useCallback(() => {
    toast({
      title: intl.formatMessage({
        defaultMessage: 'Failed to start routine setup',
        id: 'k+nJH8ounC',
        description: 'Error toast when creating the routine-setup chat fails',
      }),
      tone: 'destructive',
    });
  }, [intl]);

  const { createAndOpenProject, isSubmitting } = useCreateAndOpenProject({
    onCreateError: handleCreateError,
  });

  useEffect(() => {
    if (!isSubmitting && optimisticFirstTurnText) {
      setOptimisticFirstTurnText(null);
    }
  }, [isSubmitting, optimisticFirstTurnText]);

  const startCreate = useCallback(
    ({
      display,
      wireDescription,
      seedInstructions,
      files,
      attachment_ids,
      optimisticAttachments,
      computerUseSkill,
      approvalMode,
    }: {
      display: string;
      wireDescription: string;
      /** Agent-only instruction inserted before the `User request:` tail —
       * see `buildCreateRoutineWithHeidiWirePrompt`. Never shown in the
       * bubble; only `display` is. */
      seedInstructions?: string;
      files?: File[];
      attachment_ids?: string[];
      optimisticAttachments?: SubmitProjectTurnArgs['optimisticAttachments'];
      computerUseSkill?: SubmitProjectTurnArgs['computerUseSkill'];
      approvalMode?: SubmitProjectTurnArgs['approvalMode'];
    }) => {
      const linked = linkedItems[0] ?? null;
      const started = createAndOpenProject({
        ...getCreateRoutineWithHeidiArgs({
          description: display,
          openingMessage: intro,
        }),
        prompt: buildCreateRoutineWithHeidiWirePrompt({
          description: wireDescription,
          openingMessage: intro,
          seedInstructions,
        }),
        displayPrompt: display,
        ...(files && files.length > 0 ? { files } : {}),
        ...(attachment_ids && attachment_ids.length > 0 ? { attachment_ids } : {}),
        ...(optimisticAttachments && optimisticAttachments.length > 0
          ? { optimisticAttachments }
          : {}),
        ...(computerUseSkill ? { computerUseSkill } : {}),
        ...(approvalMode ? { approvalMode } : {}),
        ...(linked ? { linked } : {}),
      });
      if (!started) return false;
      setOptimisticFirstTurnText(display);
      return true;
    },
    [createAndOpenProject, intro, linkedItems]
  );

  const handleProvisionalSubmit = useCallback(
    (args: SubmitProjectTurnArgs): Promise<ProvisionalSubmitOutcome> => {
      const display = stripComposerPrefillMarker(args.displayText ?? args.text);
      const hasAttachments = Boolean(args.files?.length || args.attachment_ids?.length);
      if (!display && !hasAttachments) return Promise.resolve('refused');
      const started = startCreate({
        display,
        wireDescription: stripComposerPrefillMarker(args.text),
        files: args.files,
        attachment_ids: args.attachment_ids,
        optimisticAttachments: args.optimisticAttachments,
        computerUseSkill: args.computerUseSkill,
        approvalMode: args.approvalMode,
      });
      // The create seeds the opening turn; this chat has nothing left to send.
      return Promise.resolve(started ? 'consumed' : 'refused');
    },
    [startCreate]
  );

  const handleSuggestSomething = useCallback(() => {
    const display = intl.formatMessage({
      defaultMessage: 'Suggest routines for me',
      id: 'cb8N+BqbIw',
      description:
        "Opening chat bubble sent on the clinician's behalf when they click Suggest routines on the create-routine chat",
    });
    startCreate({
      display,
      wireDescription: display,
      seedInstructions: buildRoutineSuggestionSeedPrompt(),
    });
  }, [intl, startCreate]);

  const showGuidance = !isSubmitting && !optimisticFirstTurnText;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="routine-create-guided-chat"
    >
      <ProjectChat
        projectId={undefined}
        projectTitle=""
        toolbarSourcesSlot={toolbarSourcesSlot}
        provisionalFirstTurnText={optimisticFirstTurnText}
        provisionalIsSubmitting={isSubmitting}
        onProvisionalSubmit={handleProvisionalSubmit}
        timelineLeadingContent={
          <div className="mb-5 flex flex-col gap-4">
            {introBlocks.map((block, index) => (
              <div
                // Static append-only list — index keys are stable here.
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                // A gentle fade only (no slide) reads as a smooth, continuous
                // reveal rather than each block popping up into place.
                className={cn(
                  typographyVariant({ variant: 'p' }),
                  '[&_strong]:font-normal',
                  '[&>div]:text-base [&>div]:font-normal [&>div]:leading-normal [&>div]:tracking-normal',
                  'motion-safe:ease-out motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both'
                )}
                style={{ animationDuration: '600ms' }}
              >
                <AssistantText
                  text={block}
                  streaming={streaming && index === introBlocks.length - 1}
                />
              </div>
            ))}
            {showGuidance && !streaming ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit"
                onClick={handleSuggestSomething}
              >
                <FormattedMessage
                  defaultMessage="Suggest routines"
                  id="/G/+a3F/VP"
                  description="Ask Heidi to explore the clinician's own work and propose routine ideas"
                />
              </Button>
            ) : null}
          </div>
        }
      />
    </div>
  );
}
